import {
    BxfDynamicPositionEnum,
    BxfLink,
    BxfPopover,
    BxfTargetAtrEnum,
} from '@blk/bxf-react-components';
import styled from '@emotion/styled';
import { useMemo } from 'react';

import { SourcePopoverConstants } from '../constants';
import { useSourcePopoverBuilder } from '../hooks';
import { ChatBlkPromptSource, ChatBlkQuery } from '../types';

const {
    SOURCE_POPOVER_CONTENT,
    SOURCE_POPOVER_TARGET,
    DATA_CY_SOURCE_POPOVER_CONTENT,
    DATA_CY_SOURCE_POPOVER_TARGET,
} = SourcePopoverConstants;

const SourcePopoverContainer = styled.span`
    display: inline-flex;
    padding-left: 4px; // $space-xxxs
`;

const SourcePopoverContent = styled.span`
    font-size: 14px;
    line-height: 20px;
    font-weight: 700;
    width: 279px;
    padding-right: 8px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
`;

const CitationDate = styled.sup`
    padding-left: 4px;
    font-size: 12px;
`;

const SourceTitle = styled.span`
    margin: 0;
`;

const SourceSecondaryInfo = styled.span`
    font-weight: 400;
    margin: 0;
`;

interface SourcePopoverProps {
    linkNode: Element;
    href: string;
    sources: ChatBlkPromptSource[];
    query?: ChatBlkQuery;
}

/**
 * Component that renders a popover with source information.
 *
 * @param {Object} props - The properties object.
 * @param {JSX.Element} props.linkNode - The node that triggers the popover.
 * @param {string} props.href - The URL to be linked in the popover.
 * @param {Array} props.sources - The list of sources to be displayed in the popover.
 * @param {Object} props.query - The query related to the sources.
 *
 * @returns {JSX.Element} The rendered SourcePopover component.
 */
const SourcePopover = ({
    linkNode,
    href,
    sources,
    query,
}: SourcePopoverProps): JSX.Element => {
    const {
        sourceNumber,
        source,
        sourceTitle,
        secondaryInfo,
        trackSourceIconClickedTelemetry,
        trackSourcesLinkTelemetry,
        trackPopoverOpenedTelemetry,
        trackPopoverClosedTelemetry,
    } = useSourcePopoverBuilder(linkNode, href, sources, query);
    const sourceList: string[] = [];
    let previousSourceId = '';

    const getFormattedTime = (publicationTime: string) => {
        return publicationTime ? (
            <CitationDate>
                {new Intl.DateTimeFormat('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                }).format(new Date(publicationTime))}
            </CitationDate>
        ) : null;
    };

    const showDate =
        source.id !== previousSourceId && !sourceList.includes(source.id);

    if (showDate) {
        sourceList.push(source.id);
        previousSourceId = source.id;
    }

    const memoizedPopover = useMemo(
        () =>
            source ? (
                <>
                    <SourcePopoverContainer>
                        <BxfPopover
                            data-cy={DATA_CY_SOURCE_POPOVER_CONTENT}
                            dataLinkEvent={
                                'chatblk | result | footnote | close'
                            }
                            hasCloseIcon={false}
                            shouldCloseOnScroll
                            overridePlacement={
                                BxfDynamicPositionEnum.TOP_CENTER
                            }
                            onIconClicked={trackSourceIconClickedTelemetry}
                            onPopoverOpened={trackPopoverOpenedTelemetry}
                            onPopoverClosed={trackPopoverClosedTelemetry}
                        >
                            <span slot={SOURCE_POPOVER_TARGET}>
                                <BxfLink
                                    data-cy={DATA_CY_SOURCE_POPOVER_TARGET}
                                    label={sourceNumber}
                                    dataLinkEvent={`chatblk | result | footnote | open`}
                                    target={BxfTargetAtrEnum.BLANK}
                                    isCustomPopover
                                />
                            </span>
                            <div slot={SOURCE_POPOVER_CONTENT}>
                                <SourcePopoverContent
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <SourceTitle>
                                        <BxfLink
                                            href={href}
                                            label={sourceTitle[0]}
                                            dataLinkEvent={`chatblk | result | footnote | link click | ${sourceTitle[0]}`}
                                            target={BxfTargetAtrEnum.BLANK}
                                            onClick={trackSourcesLinkTelemetry}
                                        />
                                        {sourceTitle[1]}
                                    </SourceTitle>
                                    {secondaryInfo && (
                                        <SourceSecondaryInfo>
                                            {secondaryInfo}
                                        </SourceSecondaryInfo>
                                    )}
                                </SourcePopoverContent>
                            </div>
                        </BxfPopover>
                    </SourcePopoverContainer>
                    {showDate && getFormattedTime(source.publicationTime)}
                </>
            ) : (
                <></>
            ),
        [source, sourceNumber, sourceTitle, secondaryInfo, href, query, sources]
    );
    return memoizedPopover;
};

export default SourcePopover;
